/* ============================================================
   The social sign-in browser round trip, on the web: a POPUP, not a full-page
   redirect. The client core awaits `authorize()` and then exchanges the code
   itself; a redirect would unload the page and the promise with it, so the
   popup is what keeps the core's contract (and the user's unsaved state)
   intact. The popup lands on /auth/callback, which hands the code back over a
   BroadcastChannel and closes itself (src/auth/CallbackPage.tsx).

   PKCE is done here with WebCrypto, because the verifier belongs to the
   request this adapter builds. The exchange, refresh and revoke are plain
   POSTs in client-core/src/auth/oauthApi.ts.
   ============================================================ */
import type { OAuthAdapter, OAuthAuthorization } from '@habishine/client-core';

export const AUTH_CHANNEL = 'habishine-auth';
export const CALLBACK_PATH = '/auth/callback';
export const SIGNOUT_PATH = '/auth/signout';

export type AuthChannelMessage =
  | { kind: 'callback'; state: string | null; code: string | null; error: string | null; errorDescription: string | null }
  | { kind: 'signout' };

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomToken(bytes: number): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return base64url(a);
}

async function s256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

function openPopup(url: string, name: string): Window | null {
  const w = 520;
  const h = 680;
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - w) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - h) / 2));
  return window.open(url, name, `popup,width=${w},height=${h},left=${left},top=${top}`);
}

/** Wait for the popup to report back on the channel, or to be closed. */
function awaitPopup<T>(
  popup: Window,
  onMessage: (m: AuthChannelMessage, settle: (v: T) => void, fail: (e: Error) => void) => void,
  onClosed: T,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    let done = false;
    const finish = (fn: () => void) => {
      if (done) return;
      done = true;
      clearInterval(poll);
      channel.close();
      fn();
      if (!popup.closed) popup.close();
    };
    channel.onmessage = (e: MessageEvent<AuthChannelMessage>) => {
      onMessage(
        e.data,
        (v) => finish(() => resolve(v)),
        (err) => finish(() => reject(err)),
      );
    };
    // The popup closing is the only signal of a cancel — there is no event.
    const poll = window.setInterval(() => {
      if (popup.closed) finish(() => resolve(onClosed));
    }, 400);
  });
}

export const oauth: OAuthAdapter = {
  async authorize({ authorizationEndpoint, clientId, scopes, identityProvider }) {
    const codeVerifier = randomToken(48);
    const state = randomToken(16);
    const redirectUri = `${window.location.origin}${CALLBACK_PATH}`;
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: scopes.join(' '),
      identity_provider: identityProvider,
      state,
      code_challenge: await s256(codeVerifier),
      code_challenge_method: 'S256',
    });
    const popup = openPopup(`${authorizationEndpoint}?${params}`, 'habishine-sign-in');
    if (!popup) {
      throw new Error('Your browser blocked the sign-in window. Allow pop-ups for this site and try again.');
    }
    return awaitPopup<OAuthAuthorization | null>(
      popup,
      (m, settle, fail) => {
        if (m.kind !== 'callback' || m.state !== state) return;
        if (m.error) fail(new Error(m.errorDescription || m.error));
        else if (m.code) settle({ code: m.code, codeVerifier, redirectUri });
        else fail(new Error('Sign-in failed'));
      },
      null,
    );
  },

  async endSession({ endSessionEndpoint, clientId }) {
    const logoutUri = `${window.location.origin}${SIGNOUT_PATH}`;
    const params = new URLSearchParams({ client_id: clientId, logout_uri: logoutUri });
    const popup = openPopup(`${endSessionEndpoint}?${params}`, 'habishine-sign-out');
    if (!popup) return; // blocked: the local sign-out already happened
    await awaitPopup<void>(
      popup,
      (m, settle) => {
        if (m.kind === 'signout') settle(undefined);
      },
      undefined,
    );
  },
};
